import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../query-client';
import { useRef } from 'react';
import * as apis from '../../utils/web-apis-client';
import { getSimpleRemoteConfig } from '../../utils/remote-config-manager';
import { checkCoverage } from '../../utils/supplemental';
import { IAutoSave, ICoverageV1ResponseExt } from '../../data-models';
import * as dm from '../../data-models';
import { UPLOAD_ACTIVITY_TYPE } from '../../utils/upload-activities';

export const useAbsenceNotifications = () =>
  useQuery({
    queryKey: [queryKeys.absenceNotifications],
    queryFn: apis.getAbsenceNotifications,
  });

export const useLlPlanPath = (planId?: string | null) =>
  useQuery({
    queryKey: [queryKeys.llPlanPath, planId],
    queryFn: () => apis.getllPlanPath(planId ?? ''),
    enabled: !!planId,
  });

export const usePtoChoice = () =>
  useQuery({
    queryKey: [queryKeys.ptoChoice],
    queryFn: () => apis.getPtoChoice(),
  });

/**
 * UPLOAD activities, refetched by the upload poller at its checkpoints rather
 * than on an interval
 */
export const useUploadActivities = () =>
  useQuery({
    queryKey: [queryKeys.activities, UPLOAD_ACTIVITY_TYPE],
    queryFn: () => apis.getActivities(UPLOAD_ACTIVITY_TYPE),
  });

export const useActivities = (
  activityType: string,
  processedStatus?: boolean,
) => {
  const refetchCountRef = useRef(0);

  return useQuery({
    queryKey: [queryKeys.activities, activityType, processedStatus],
    queryFn: () => apis.getActivities(activityType, processedStatus),
    refetchInterval: (_data, query) => {
      // If there's an error, use longer interval
      if (query.state.error) {
        return 60000;
      }

      // After 3 successful refetches, switch to longer interval
      if (refetchCountRef.current >= 2) {
        return 60000; // 60 seconds
      }

      return 5000; // 5 seconds for initial fetches
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    cacheTime: 300000,
    onSuccess: () => {
      refetchCountRef.current += 1;
    },
    onError: () => {
      // Reset counter on error so next mount starts fresh
      refetchCountRef.current = 0;
    },
  });
};

export const useAutosaveGet = (formName?: string) =>
  useQuery({
    queryKey: [queryKeys.autosaveGet, formName],
    queryFn: (_context) => apis.getAutoSave(formName ?? ''),
    enabled: !!formName,
  });

export const useAutosaveGetFieldValue = (
  formName?: string,
  fieldValue?: string,
) =>
  useQuery({
    queryKey: [queryKeys.autosaveGet, formName],
    queryFn: (_context) => apis.getAutoSave(formName ?? ''),
    select: (data) => {
      if (data?.exists) {
        return data.result.data[fieldValue ?? ''];
      }
      return undefined;
    },
    enabled: !!formName && !!fieldValue,
  });

export const useAutosavePost = (formName: string) => {
  return useMutation({
    mutationKey: [queryKeys.autosavePost, formName],
    mutationFn: (form: IAutoSave) => apis.postAutoSave(form),
    onSuccess: (_data, autosaveData) => {
      if (autosaveData.submitted) {
        queryClient.invalidateQueries([queryKeys.autosaveGet, formName]);
      } else {
        queryClient.setQueryData([queryKeys.autosaveGet, formName], {
          result: autosaveData,
          exists: true,
        });
      }
    },
  });
};

export const useAutosaveDelete = (formName: string) =>
  useMutation({
    mutationKey: [queryKeys.autosaveDelete, formName],
    mutationFn: () => apis.deleteAutoSave(formName),
  });

export const useAvailableCallbackTimes = (
  companyCode: string,
  queryDate: string,
) =>
  useQuery({
    queryKey: [queryKeys.availableCallbackTimes, companyCode, queryDate],
    queryFn: (_context) =>
      apis.getAvailableCallbackTimes(companyCode, queryDate),
    enabled: !!companyCode && !!queryDate,
  });

export const useGetAbsencePeriodDecisions = (caseIds: string[]) =>
  useQuery({
    queryKey: [queryKeys.absencePeriodDecisions, ...caseIds],
    queryFn: async () => {
      const results = await Promise.all(
        caseIds.map((id) => apis.getAbsencePeriodDecisions(id)),
      );
      return results.flat();
    },
    enabled: caseIds.length > 0,
  });

export const useGetAccommodationCases = (accommodationCaseIds: string[]) =>
  useQuery<dm.AccommodationCase[]>({
    queryKey: [
      queryKeys.accommodationCases,
      ...[...new Set(accommodationCaseIds)].sort(),
    ],
    queryFn: async () => {
      const results = await Promise.all(
        accommodationCaseIds.map((id) => apis.getAccommodationCase(id)),
      );
      return results;
    },
    enabled: accommodationCaseIds.length > 0,
  });

export const useCases = () =>
  useQuery({
    queryKey: [queryKeys.cases],
    queryFn: apis.getCases,
  });

export const useCaseHistory = (ntn?: string) =>
  useQuery({
    queryKey: [queryKeys.caseHistory, ntn],
    queryFn: () => apis.getCaseHistory(ntn!),
    enabled: !!ntn,
  });

export const useDraftClaims = () =>
  useQuery({
    queryKey: [queryKeys.draftClaims],
    queryFn: apis.getDraftClaims,
  });

export const useEmployerPreferences = () =>
  useQuery({
    queryKey: [queryKeys.employerPreferences],
    queryFn: apis.getEmployerPreferences,
  });

export const useLeaveScenarios = (
  formName: string,
  // leaveType: string,
  // abs_reason: string,
  // reasonQualifier1: string,
) =>
  useQuery({
    queryKey: [
      queryKeys.leaveScenarios,
      formName,
      // leaveType,
      // abs_reason,
      // reasonQualifier1,
    ],
    queryFn: () =>
      apis.getCorpLeaveIncrements(
        formName,
        // leaveType,
        // abs_reason,
        // reasonQualifier1,
      ),
  });

export const usePaymentPreferences = () =>
  useQuery({
    queryKey: [queryKeys.paymentPreferences],
    queryFn: apis.getPaymentPreferences,
  });

export const usePerson = () =>
  useQuery({
    queryKey: [queryKeys.person],
    queryFn: apis.getPerson,
  });

export const usePersonData = () =>
  useQuery({
    queryKey: [queryKeys.personData],
    queryFn: apis.getPersonData,
  });

export const usePreferences = () =>
  useQuery({
    queryKey: [queryKeys.preferences],
    queryFn: apis.getPreferences,
  });

export const useRemoteConfig = () =>
  useQuery({
    queryKey: [queryKeys.remoteConfig],
    queryFn: getSimpleRemoteConfig,
  });

export const useTasksGet = (queryParams: any) =>
  useQuery({
    queryKey: [queryKeys.tasksGet, queryParams],
    queryFn: () => apis.getTasks(queryParams),
    enabled:
      queryParams !== undefined && queryParams !== null && queryParams !== '',
    retry: 1,
  });

export const useVBCoverage = () =>
  useQuery({
    queryKey: [queryKeys.vbCoverage],
    queryFn: apis.getVBCoverage,
    select: (data) =>
      ({ ...data, hasCoverage: checkCoverage(data) } as ICoverageV1ResponseExt),
  });

export const useVBClaims = (enabled = true) =>
  useQuery({
    queryKey: [queryKeys.vbClaims],
    queryFn: apis.getVBClaims,
    select: (data) => data.claims,
    enabled,
    staleTime: 300000,
    cacheTime: 300000,
  });

export const queryKeys = {
  absenceNotifications: 'absenceNotifications',
  llPlanPath: 'llPlanPath',
  ptoChoice: 'ptoChoice',
  absencePeriodDecisions: 'absencePeriodDecisions',
  accommodationCases: 'accommodationCases',
  activities: 'activities',
  autosaveDelete: 'autosaveDelete',
  autosaveGet: 'autosaveGet',
  autosavePost: 'autosavePost',
  availableCallbackTimes: 'availableCallbackTimes',
  cases: 'cases',
  disability: 'disability',
  draftClaims: 'draftClaims',
  employerPreferences: 'employerPreferences',
  leaveScenarios: 'leaveScenarios',
  paymentPreferences: 'paymentPreferences',
  person: 'person',
  personData: 'personData',
  preferences: 'preferences',
  remoteConfig: 'remoteConfig',
  tasksGet: 'tasksGet',
  vbClaims: 'vbClaims',
  vbCoverage: 'vbCoverage',
  caseHistory: 'caseHistory',
} as const;
